import {
  QUADRO_DA_FACE,
  TAMANHOS_DA_MARCACAO,
  centroDaMarcacao,
  regiaoNoPonto,
  type MarcacaoFacial,
  type TamanhoDaMarcacao,
  type TipoDeMarcacao,
  type VistaDaFace,
} from "@bookdental/schemas";

// Contas das alças do editor da face. Tudo normalizado (0–1); medidas de tamanho em fração da largura,
// por isso a altura entra convertida (H/W) quando a conta mistura os dois eixos.

const W = QUADRO_DA_FACE.largura;
const H = QUADRO_DA_FACE.altura;
export const limitar = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

export type Alca = "mover" | "raio" | "rx" | "ry" | "giro" | "vertice";
export type Arraste = { alca: Alca; indice: number; vertice?: number; inicio: [number, number]; original: MarcacaoFacial; regiaoAuto: string | null };

/** Posição (px) de cada alça da marcação selecionada. */
export function alcasDa(m: MarcacaoFacial): { alca: Alca; vertice?: number; x: number; y: number }[] {
  if (m.tipo === "TRAJETO") return m.pontos.map(([x, y], vertice) => ({ alca: "vertice", vertice, x: x * W, y: y * H }));
  const cx = m.x * W;
  const cy = m.y * H;
  if (m.tipo === "AREA") return [{ alca: "raio", x: cx + m.raio * W, y: cy }];
  if (m.tipo === "ELIPSE") {
    const a = (m.rotacao * Math.PI) / 180;
    const giro = (dx: number, dy: number) => ({ x: cx + dx * Math.cos(a) - dy * Math.sin(a), y: cy + dx * Math.sin(a) + dy * Math.cos(a) });
    return [
      { alca: "rx", ...giro(m.rx * W, 0) },
      { alca: "ry", ...giro(0, m.ry * W) },
      { alca: "giro", ...giro(0, -(m.ry * W + 26)) },
    ];
  }
  return [];
}

const autoDe = (m: MarcacaoFacial) => {
  const [x, y] = centroDaMarcacao(m);
  return regiaoNoPonto(m.vista, x, y)?.codigo ?? null;
};

export const iniciarArraste = (alca: Alca, indice: number, m: MarcacaoFacial, inicio: [number, number], vertice?: number): Arraste => ({
  alca,
  indice,
  vertice,
  inicio,
  original: m,
  regiaoAuto: autoDe(m),
});

/** A marcação depois de arrastar até `p`. Mover leva a região junto quando ela era a sugerida. */
export function arrastar(a: Arraste, [px, py]: [number, number]): MarcacaoFacial {
  const o = a.original;
  if (a.alca === "mover") {
    const dx = px - a.inicio[0];
    const dy = py - a.inicio[1];
    const movida: MarcacaoFacial =
      o.tipo === "TRAJETO"
        ? { ...o, pontos: o.pontos.map(([x, y]): [number, number] => [limitar(x + dx), limitar(y + dy)]) }
        : { ...o, x: limitar(o.x + dx), y: limitar(o.y + dy) };
    return o.regiao === a.regiaoAuto ? { ...movida, regiao: autoDe(movida) } : movida;
  }
  if (a.alca === "vertice" && o.tipo === "TRAJETO") {
    return { ...o, pontos: o.pontos.map((pt, i): [number, number] => (i === a.vertice ? [px, py] : pt)) };
  }
  if (o.tipo === "TRAJETO" || o.tipo === "PONTO") return o;
  const dxPx = (px - o.x) * W;
  const dyPx = (py - o.y) * H;
  if (a.alca === "raio" && o.tipo === "AREA") return { ...o, raio: limitar(Math.hypot(dxPx, dyPx) / W, 0.01, 0.3) };
  if (o.tipo !== "ELIPSE") return o;
  const ang = (o.rotacao * Math.PI) / 180;
  if (a.alca === "rx") return { ...o, rx: limitar(Math.abs(dxPx * Math.cos(ang) + dyPx * Math.sin(ang)) / W, 0.01, 0.3) };
  if (a.alca === "ry") return { ...o, ry: limitar(Math.abs(-dxPx * Math.sin(ang) + dyPx * Math.cos(ang)) / W, 0.01, 0.3) };
  let graus = (Math.atan2(dyPx, dxPx) * 180) / Math.PI + 90;
  if (graus > 180) graus -= 360;
  return { ...o, rotacao: Math.round(graus) };
}

/** Desloca a marcação pelo teclado (setas). */
export const deslocar = (m: MarcacaoFacial, dx: number, dy: number): MarcacaoFacial =>
  m.tipo === "TRAJETO"
    ? { ...m, pontos: m.pontos.map(([x, y]): [number, number] => [limitar(x + dx), limitar(y + dy)]) }
    : { ...m, x: limitar(m.x + dx), y: limitar(m.y + dy) };

/** Marcação nova no clique, já com a região sugerida (o trajeto nasce dos pontos do rascunho). */
export function novaMarcacao(tipo: Exclude<TipoDeMarcacao, "TRAJETO">, tamanho: TamanhoDaMarcacao, vista: VistaDaFace, x: number, y: number): MarcacaoFacial {
  const regiao = regiaoNoPonto(vista, x, y)?.codigo ?? null;
  const r = TAMANHOS_DA_MARCACAO[tamanho];
  if (tipo === "AREA") return { tipo, vista, x, y, raio: r, regiao };
  if (tipo === "ELIPSE") return { tipo, vista, x, y, rx: r, ry: Math.round(r * 550) / 1000, rotacao: 0, regiao };
  return { tipo, vista, x, y, regiao };
}

/** Aplica um tamanho rápido à marcação (ponto e trajeto não têm tamanho). */
export function comTamanho(m: MarcacaoFacial, tamanho: TamanhoDaMarcacao): MarcacaoFacial {
  const r = TAMANHOS_DA_MARCACAO[tamanho];
  if (m.tipo === "AREA") return { ...m, raio: r };
  if (m.tipo === "ELIPSE") return { ...m, rx: r, ry: Math.round(r * 550) / 1000 };
  return m;
}
